import ActionBar from '../../ActionBar/NewActionBar';
import Constants from 'expo-constants';
import ContentProgressBar from '../ContentProgressBar/ContentProgressBar';
import Show from '../../Show/Show';
import { COLORS } from '../../../config/designSystem/Colors';
import { FC, PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { KeyboardAvoidingView, ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient, LinearGradientProps } from 'expo-linear-gradient';
import { SPACING } from '../../../config/designSystem/Spacing';
import { StatusBar } from 'expo-status-bar';
import { isWeb } from '../../../config/designSystem/Layout';
import { observer } from 'mobx-react';
import { useStores } from '../../../hooks/useStores';
import { useVerticalOffsetHeight } from '../../../hooks/ui/useVerticalOffsetHeight';

interface ContentFrameProps extends PropsWithChildren {
  handleExit?: () => void;
  handleOnPrevious?: () => void;
  title?: string;
  showProgressBar?: boolean;
  linearGradient?: LinearGradientProps;
  /** rendered below the scrolling content, pinned above the bottom edge
   *  (and lifted with the keyboard) — for buttons that must stay reachable */
  footer?: ReactNode;
  /** the scroll position is reset to the top whenever this changes: one
   *  ScrollView serves every frame of a session, so without it a long frame
   *  opened after a scrolled one started at the bottom */
  scrollResetKey?: string | number;
}
const ACTION_BAR_HEIGHT = 40;
const PROGRESS_BAR_ALLOWANCE = 16;

const _ContentFrame: FC<ContentFrameProps> = ({
  children,
  handleExit,
  handleOnPrevious,
  title,
  showProgressBar = true,
  linearGradient,
  footer,
  scrollResetKey,
}) => {

  const { treeNav } = useStores();
  const verticalOffSetHeight = useVerticalOffsetHeight();
  const scrollRef = useRef<ScrollView>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [scrollResetKey]);

  const frameContent = (
    <>
      <StatusBar style="dark" />
      <Show when={showProgressBar}>
        <ContentProgressBar />
      </Show>
      <ActionBar
        width='100%'
        onPrevious={handleOnPrevious}
        height={40}
        title={title}
        onExit={handleExit}
        backButtonDisabled={treeNav.stepBackDisabled}
      />
      <KeyboardAvoidingView
        behavior='padding'
        style={styles.KeyboardAvoidingView}
        // keyboardVerticalOffset must equal the distance from the top of the screen
        // to this view. useVerticalOffsetHeight() returns 0 on every platform (correct
        // for the public screens, where the KAV fills the screen), but ContentFrame stacks
        // the status bar padding, optional progress bar and the 40pt ActionBar above
        // its KAV -- with 0 the wizard's Continue button sat under the keyboard.
        // A slight overshoot is safe (content floats higher, never clipped).
        // With a footer that overshoot is not wanted: the frame fills the screen, the
        // KAV's own layout already accounts for what sits above it, and the extra
        // offset left the footer button floating ~100pt above the keyboard.
        keyboardVerticalOffset={verticalOffSetHeight + (isWeb || footer ? 0 : Constants.statusBarHeight + ACTION_BAR_HEIGHT + (showProgressBar ? PROGRESS_BAR_ALLOWANCE : 0))}
      >
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={[styles.ScrollContent, !!footer && styles.ScrollContentWithFooter]}
          // taps inside this scrollview handle navigation rather than dismiss keyboard
          keyboardShouldPersistTaps='handled'
        >
          {children}
        </ScrollView>
        {footer}
      </KeyboardAvoidingView>
    </>
  );

  return linearGradient ? (
    <LinearGradient {...linearGradient} style={styles.Frame} id='ContentFrame'>
      {frameContent}
    </LinearGradient>
  ) : (
    <View style={styles.Frame} id='ContentFrame'>
      {frameContent}
    </View>
  );
};
export const ContentFrame = observer(_ContentFrame);

const styles = StyleSheet.create({
  Frame: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: isWeb ? 0 : Constants.statusBarHeight,
    paddingBottom: SPACING['sp-32'],
    paddingHorizontal: SPACING['sp-16'],
    backgroundColor: COLORS.surface.primary,
    opacity: 1,
  },
  KeyboardAvoidingView: {
    flex: 1,
    width: '100%',
  },
  ScrollContent: {
    flexGrow: 1,
    // room for the overlay session buttons (see SessionButtons)
    paddingBottom: SPACING['sp-64'],
  },
  ScrollContentWithFooter: {
    paddingBottom: SPACING['sp-16'],
  },
});
